import { useState, useEffect, useRef } from "react";
import { C, FMP_KEY, FMP_BASE } from "../constants";
import PageHeader from "../components/PageHeader";
import SiteFooter from "../components/SiteFooter";

// ── Black-Scholes ─────────────────────────────────────────────────────────────

function normCDF(x) {
  const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  const s=x<0?-1:1; const ax=Math.abs(x);
  const t=1/(1+p*ax);
  const y=1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-ax*ax/2);
  return 0.5*(1+s*y);
}
function normPDF(x){return Math.exp(-x*x/2)/Math.sqrt(2*Math.PI);}

function bsPrice(S,K,T,r,sig,type){
  if(T<=0||sig<=0) return type==='call'?Math.max(0,S-K):Math.max(0,K-S);
  const sq=Math.sqrt(T);
  const d1=(Math.log(S/K)+(r+sig*sig/2)*T)/(sig*sq);
  const d2=d1-sig*sq;
  return type==='call'
    ?Math.max(0,S*normCDF(d1)-K*Math.exp(-r*T)*normCDF(d2))
    :Math.max(0,K*Math.exp(-r*T)*normCDF(-d2)-S*normCDF(-d1));
}

function bsGreeks(S,K,T,r,sig,type){
  if(T<=0||sig<=0) return{delta:0,gamma:0,theta:0,vega:0};
  const sq=Math.sqrt(T);
  const d1=(Math.log(S/K)+(r+sig*sig/2)*T)/(sig*sq);
  const d2=d1-sig*sq;
  const nd1=normPDF(d1);
  const delta=type==='call'?normCDF(d1):normCDF(d1)-1;
  const gamma=nd1/(S*sig*sq);
  const vega=S*nd1*sq/100;
  const theta=type==='call'
    ?(-(S*nd1*sig)/(2*sq)-r*K*Math.exp(-r*T)*normCDF(d2))/365
    :(-(S*nd1*sig)/(2*sq)+r*K*Math.exp(-r*T)*normCDF(-d2))/365;
  return{delta,gamma,theta,vega};
}

// ── Strategies ────────────────────────────────────────────────────────────────
// stkOff: multiples of 5% offset from ATM (0=ATM, 1=+5%, -1=-5%, etc.)

const STRATS=[
  {key:'call',          label:'Long Call',        emoji:'🎰', tag:'0DTE Lottery',
   desc:'Unlimited upside, can expire worthless. The WSB special — cheap tickets to the moon.',
   legs:[{type:'call', action:'buy', stkOff:0}]},
  {key:'put',           label:'Long Put',         emoji:'📉', tag:'Short Thesis',
   desc:'Profits as the stock falls. Defined max loss (premium paid), unlimited downside capture.',
   legs:[{type:'put', action:'buy', stkOff:0}]},
  {key:'coveredcall',   label:'Covered Call',     emoji:'📤', tag:'Rent Collector',
   desc:'Own 100 shares + sell a call above market. Collect premium every cycle, cap your upside.',
   legs:[{type:'stock',action:'buy'},{type:'call',action:'sell',stkOff:1}]},
  {key:'protectiveput', label:'Protective Put',   emoji:'🛡️', tag:'Crash Insurance',
   desc:'Own 100 shares + buy a put below market. Keep all the upside, hard floor on losses.',
   legs:[{type:'stock',action:'buy'},{type:'put',action:'buy',stkOff:-1}]},
  {key:'bullcall',      label:'Bull Call Spread',  emoji:'⬆️', tag:'Cheap Bull Bet',
   desc:'Buy a lower call, sell a higher call. Cheaper than a naked call — defined risk and reward.',
   legs:[{type:'call',action:'buy',stkOff:0},{type:'call',action:'sell',stkOff:1}]},
  {key:'bearput',       label:'Bear Put Spread',   emoji:'⬇️', tag:'Cheap Bear Bet',
   desc:'Buy a higher put, sell a lower put. Defined-risk way to profit from a pullback.',
   legs:[{type:'put',action:'buy',stkOff:0},{type:'put',action:'sell',stkOff:-1}]},
  {key:'ironcondor',    label:'Iron Condor',       emoji:'🦅', tag:'Theta Farmer',
   desc:'Sell a put spread + a call spread. Collect premium if the stock stays range-bound.',
   legs:[
     {type:'put', action:'buy', stkOff:-2},
     {type:'put', action:'sell',stkOff:-1},
     {type:'call',action:'sell',stkOff:1},
     {type:'call',action:'buy', stkOff:2},
   ]},
];

function strikeInterval(S){
  return S<10?0.5:S<30?1:S<75?2.5:S<200?5:S<600?10:25;
}
function roundStrike(raw,S){
  const iv=strikeInterval(S);
  return parseFloat((Math.round(raw/iv)*iv).toFixed(2));
}
function defaultStrikes(S,stratKey){
  const strat=STRATS.find(s=>s.key===stratKey);
  if(!strat) return [];
  return strat.legs.filter(l=>l.type!=='stock').map(l=>roundStrike(S*(1+l.stkOff*0.05),S));
}

// ── P&L engine ────────────────────────────────────────────────────────────────

function computePnL(S0,strikes,dte,iv,rfRate,stratKey,contracts){
  const strat=STRATS.find(s=>s.key===stratKey);
  if(!strat||!S0) return null;

  const T=Math.max(dte/365,0);
  const r=rfRate/100;
  const sig=Math.max(iv/100,0.01);
  const mult=contracts*100;

  const optLegs=strat.legs.filter(l=>l.type!=='stock');
  const stockLegs=strat.legs.filter(l=>l.type==='stock');

  // Entry prices at current stock price
  const entryPrices=optLegs.map((leg,i)=>bsPrice(S0,strikes[i],T,r,sig,leg.type));

  // Net debit paid (positive = you paid, negative = you received credit)
  let netDebit=0;
  optLegs.forEach((leg,i)=>{netDebit+=(leg.action==='buy'?1:-1)*entryPrices[i]*mult;});

  // P&L over a ±40% price range
  const N=160;
  const lo=S0*0.60, hi=S0*1.40;
  const points=[];

  for(let i=0;i<=N;i++){
    const S=lo+(hi-lo)*(i/N);

    let pnlExp=0;
    optLegs.forEach((leg,j)=>{
      const intrinsic=leg.type==='call'?Math.max(0,S-strikes[j]):Math.max(0,strikes[j]-S);
      pnlExp+=(leg.action==='buy'?1:-1)*(intrinsic-entryPrices[j])*mult;
    });
    stockLegs.forEach(leg=>{pnlExp+=(leg.action==='buy'?1:-1)*(S-S0)*mult;});

    let pnlNow=0;
    optLegs.forEach((leg,j)=>{
      const currV=bsPrice(S,strikes[j],T,r,sig,leg.type);
      pnlNow+=(leg.action==='buy'?1:-1)*(currV-entryPrices[j])*mult;
    });
    stockLegs.forEach(leg=>{pnlNow+=(leg.action==='buy'?1:-1)*(S-S0)*mult;});

    points.push({S,pnlExp,pnlNow});
  }

  // Aggregate Greeks at S0
  let dDelta=0,dGamma=0,dTheta=0,dVega=0;
  optLegs.forEach((leg,i)=>{
    const g=bsGreeks(S0,strikes[i],T,r,sig,leg.type);
    const sgn=leg.action==='buy'?1:-1;
    dDelta+=sgn*g.delta*mult;
    dGamma+=sgn*g.gamma*mult;
    dTheta+=sgn*g.theta*mult;
    dVega +=sgn*g.vega *mult;
  });
  stockLegs.forEach(leg=>{dDelta+=(leg.action==='buy'?1:-1)*mult;});

  // Breakevens (zero crossings of expiry P&L)
  const bes=[];
  for(let i=1;i<points.length;i++){
    const a=points[i-1].pnlExp, b=points[i].pnlExp;
    if((a<0&&b>=0)||(a>=0&&b<0)){
      const frac=Math.abs(a)/(Math.abs(a)+Math.abs(b));
      bes.push(points[i-1].S+frac*(points[i].S-points[i-1].S));
    }
  }

  const expPnLs=points.map(p=>p.pnlExp);
  const maxProfit=Math.max(...expPnLs);
  const maxLoss=Math.min(...expPnLs);

  // Theta decay: hold S=S0, vary T from today down to 0
  const decayPts=[];
  if(dte>0){
    const decayN=Math.min(dte,180);
    for(let d=0;d<=decayN;d++){
      const Td=(dte-d*(dte/decayN))/365;
      let val=0;
      optLegs.forEach((leg,i)=>{
        const curr=bsPrice(S0,strikes[i],Math.max(Td,0),r,sig,leg.type);
        val+=(leg.action==='buy'?1:-1)*(curr-entryPrices[i])*mult;
      });
      decayPts.push({d:Math.round(d*(dte/decayN)),val});
    }
  }

  return{
    points,
    greeks:{delta:dDelta,gamma:dGamma,theta:dTheta,vega:dVega},
    netDebit,
    maxProfit:maxProfit>99999?null:maxProfit,
    maxLoss:maxLoss<-99999?null:maxLoss,
    breakevens:bes,
    decayPts,
  };
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtPnL(v,showSign=true){
  if(v==null) return'—';
  const abs=Math.abs(v);
  const sign=v>=0?(showSign?'+':''):'-';
  if(abs>=10000) return`${sign}$${(abs/1000).toFixed(1)}K`;
  if(abs>=100)   return`${sign}$${abs.toFixed(0)}`;
  return`${sign}$${abs.toFixed(2)}`;
}
function fmtS(v){return v==null?'—':`$${Number(v).toFixed(2)}`;}

// ── Main Component ────────────────────────────────────────────────────────────

export default function OptionsPage(){
  const [searchInput,setSearchInput]=useState('');
  const [ticker,setTicker]=useState('');
  const [stockPrice,setStockPrice]=useState(null);
  const [stockName,setStockName]=useState('');
  const [fetchLoading,setFetchLoading]=useState(false);
  const [fetchErr,setFetchErr]=useState('');
  const [strategy,setStrategy]=useState('call');
  const [strikes,setStrikes]=useState([]);
  const [dte,setDte]=useState(14);
  const [iv,setIv]=useState(35);
  const [contracts,setContracts]=useState(1);
  const rfRate=4.3;
  const [hoverS,setHoverS]=useState(null);
  const chartRef=useRef(null);

  useEffect(()=>{
    if(stockPrice) setStrikes(defaultStrikes(stockPrice,strategy));
  },[strategy,stockPrice]);

  async function searchStock(e){
    e?.preventDefault();
    const t=searchInput.trim().toUpperCase().replace(/[^A-Z0-9.]/g,'');
    if(!t) return;
    setFetchLoading(true); setFetchErr('');
    try{
      const r=await fetch(`${FMP_BASE}/stable/quote?symbol=${t}&apikey=${FMP_KEY}`);
      const data=await r.json();
      const q=Array.isArray(data)?data[0]:data;
      if(!q?.price) throw new Error('no data');
      setTicker(t); setStockPrice(q.price); setStockName(q.name||t);
      setStrikes(defaultStrikes(q.price,strategy));
    }catch{
      setFetchErr(`No data found for "${searchInput.trim()}"`);
    }
    setFetchLoading(false);
  }

  const strat=STRATS.find(s=>s.key===strategy)||STRATS[0];
  const optLegs=strat.legs.filter(l=>l.type!=='stock');
  const hasStock=strat.legs.some(l=>l.type==='stock');

  const pnlData=(stockPrice&&strikes.length===optLegs.length)
    ?computePnL(stockPrice,strikes,dte,iv,rfRate,strategy,contracts)
    :null;

  const {points=[],greeks={delta:0,gamma:0,theta:0,vega:0},netDebit=0,maxProfit=null,maxLoss=null,breakevens=[],decayPts=[]}=pnlData||{};

  // ── Chart geometry ─────────────────────────────────────────────────────────
  const W=560,H=240,PL=58,PR=16,PT=14,PB=46;
  const cW=W-PL-PR, cH=H-PT-PB;

  const chartLo=points[0]?.S||0;
  const chartHi=points[points.length-1]?.S||1;
  const chartRange=chartHi-chartLo||1;
  const xP=S=>PL+((S-chartLo)/chartRange)*cW;

  let minPnL=0, maxPnL=0;
  if(points.length){
    const all=[...points.map(p=>p.pnlExp),...points.map(p=>p.pnlNow)];
    minPnL=Math.min(0,...all); maxPnL=Math.max(0,...all);
    const pad=Math.max((maxPnL-minPnL)*0.18,contracts*20);
    minPnL-=pad; maxPnL+=pad;
  }
  const pnlRange=maxPnL-minPnL||1;
  const yP=v=>PT+cH-((v-minPnL)/pnlRange)*cH;
  const y0=yP(0);

  const expPath=points.map((p,i)=>`${i===0?'M':'L'}${xP(p.S).toFixed(1)},${yP(p.pnlExp).toFixed(1)}`).join(' ');
  const nowPath=points.map((p,i)=>`${i===0?'M':'L'}${xP(p.S).toFixed(1)},${yP(p.pnlNow).toFixed(1)}`).join(' ');

  // Y-axis ticks
  const yTicks=[];
  if(points.length){
    const rough=(maxPnL-minPnL)/5;
    const mag=Math.pow(10,Math.floor(Math.log10(Math.max(rough,1))));
    const step=Math.ceil(rough/mag)*mag;
    for(let v=Math.ceil(minPnL/step)*step;v<=maxPnL+step*0.5;v+=step) yTicks.push(v);
  }

  const xLabels=points.length?[0,.25,.5,.75,1].map(f=>({S:chartLo+f*chartRange,x:PL+f*cW})):[];

  // Hover
  function handleChartMove(e){
    if(!points.length||!chartRef.current) return;
    const rect=chartRef.current.getBoundingClientRect();
    const clientX=e.touches?e.touches[0].clientX:e.clientX;
    const pct=(clientX-rect.left)/(rect.width);
    const svgX=pct*W;
    const S=chartLo+((svgX-PL)/cW)*chartRange;
    if(S>=chartLo&&S<=chartHi) setHoverS(S);
    else setHoverS(null);
  }
  const hoverPt=hoverS&&points.length?points.reduce((b,p)=>Math.abs(p.S-hoverS)<Math.abs(b.S-hoverS)?p:b,points[0]):null;

  // Decay chart geometry
  const DW=420,DH=90,DPL=8,DPR=8,DPT=8,DPB=26;
  const dcW=DW-DPL-DPR, dcH=DH-DPT-DPB;
  const dVals=decayPts.map(p=>p.val);
  const dMin=Math.min(0,...dVals), dMax=Math.max(0,...dVals);
  const dRange=dMax-dMin||1;
  const dxP=d=>DPL+(d/Math.max(dte,1))*dcW;
  const dyP=v=>DPT+dcH-((v-dMin)/dRange)*dcH;
  const dy0=dyP(0);
  const decayPath=decayPts.map((p,i)=>`${i===0?'M':'L'}${dxP(p.d).toFixed(1)},${dyP(p.val).toFixed(1)}`).join(' ');
  const decayColor=greeks.theta<0?C.neg:C.green;

  return(
    <>
      <PageHeader active="options"/>
      <style>{`
        .opt-page{max-width:1180px;margin:0 auto;padding:32px 22px 100px}
        .opt-strip{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;-webkit-overflow-scrolling:touch}
        .opt-strip::-webkit-scrollbar{display:none}
        .opt-btn{flex-shrink:0;display:flex;flex-direction:column;align-items:flex-start;gap:4px;padding:11px 14px;border-radius:8px;border:1px solid rgba(201,168,76,0.28);background:#111;cursor:pointer;min-width:142px;transition:border-color .15s,background .15s;text-align:left}
        .opt-btn.sel{border-color:${C.gold};background:rgba(201,168,76,0.13)}
        .opt-btn:hover:not(.sel){border-color:rgba(201,168,76,0.55);background:#161616}
        .opt-grid{display:grid;grid-template-columns:290px 1fr;gap:22px;align-items:start}
        .opt-card{background:#111;border:1px solid rgba(201,168,76,0.28);border-radius:12px;padding:20px}
        .opt-slider{-webkit-appearance:none;appearance:none;width:100%;height:5px;background:rgba(201,168,76,0.45);border-radius:3px;outline:none;cursor:pointer;margin:0;box-shadow:0 0 8px rgba(201,168,76,0.25)}
        .opt-slider::-webkit-slider-thumb{-webkit-appearance:none;width:20px;height:20px;border-radius:50%;background:linear-gradient(135deg,${C.gold3},${C.gold});cursor:pointer;border:2px solid #000;box-shadow:0 0 10px rgba(232,200,117,0.70),0 0 4px rgba(201,168,76,0.50)}
        .opt-slider::-moz-range-thumb{width:20px;height:20px;border-radius:50%;background:linear-gradient(135deg,${C.gold3},${C.gold});cursor:pointer;border:2px solid #000;box-shadow:0 0 10px rgba(232,200,117,0.70),0 0 4px rgba(201,168,76,0.50);box-sizing:border-box}
        .opt-greek{flex:1;min-width:0;background:#111;border:1px solid rgba(201,168,76,0.28);border-radius:10px;padding:14px 16px}
        .opt-stat{background:#111;border:1px solid rgba(201,168,76,0.28);border-radius:8px;padding:12px 14px}
        @media(max-width:768px){
          .opt-page{padding:20px 14px 90px}
          .opt-grid{grid-template-columns:1fr}
          .opt-greek{min-width:calc(50% - 6px)}
        }
      `}</style>

      <div className="opt-page">

        {/* Title */}
        <div style={{marginBottom:28}}>
          <div className="hma-page-label">Options Lab</div>
          <div className="hma-page-title">What's Your Play?</div>
          <div style={{fontFamily:"'Space Mono',monospace",fontSize:13,color:C.text,marginTop:6,lineHeight:1.6}}>
            Model hedges, spreads, or 0DTE yolos. Drag the sliders. See the math.
          </div>
        </div>

        {/* Search */}
        <form onSubmit={searchStock} style={{display:'flex',gap:10,marginBottom:32,maxWidth:480}}>
          <input
            value={searchInput}
            onChange={e=>setSearchInput(e.target.value.toUpperCase())}
            placeholder="Ticker — AAPL, NVDA, SPY…"
            style={{flex:1,background:'#111',border:'1px solid rgba(201,168,76,0.40)',borderRadius:8,padding:'12px 16px',color:C.text,fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,letterSpacing:'.04em',outline:'none'}}
          />
          <button type="submit" disabled={fetchLoading}
            style={{padding:'12px 22px',background:`linear-gradient(135deg,#8a5a10,${C.gold},${C.gold2})`,color:'#000',fontFamily:"'Syne',sans-serif",fontSize:11,fontWeight:800,letterSpacing:'.12em',textTransform:'uppercase',border:'none',borderRadius:8,cursor:fetchLoading?'wait':'pointer',flexShrink:0,opacity:fetchLoading?0.7:1}}>
            {fetchLoading?'…':'Run'}
          </button>
        </form>
        {fetchErr&&<div style={{color:C.neg,fontFamily:"'Space Mono',monospace",fontSize:13,marginBottom:16}}>{fetchErr}</div>}

        {stockPrice&&<>

          {/* Stock badge */}
          <div style={{display:'flex',alignItems:'baseline',gap:12,marginBottom:22,flexWrap:'wrap'}}>
            <span style={{fontFamily:"'Syne',sans-serif",fontSize:22,fontWeight:800,color:C.gold2}}>{ticker}</span>
            <span style={{color:C.soft,fontSize:13,fontFamily:"'Syne',sans-serif"}}>{stockName}</span>
            <span style={{fontFamily:"'Space Mono',monospace",fontSize:15,fontWeight:700,color:C.text,marginLeft:'auto'}}>{fmtS(stockPrice)}</span>
          </div>

          {/* Strategy strip */}
          <div className="opt-strip" style={{marginBottom:12}}>
            {STRATS.map(s=>(
              <button key={s.key} className={`opt-btn${strategy===s.key?' sel':''}`} onClick={()=>setStrategy(s.key)}>
                <span style={{fontSize:15}}>{s.emoji}</span>
                <span style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:700,color:strategy===s.key?C.gold2:C.text,lineHeight:1.2}}>{s.label}</span>
                <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:strategy===s.key?C.gold:'rgba(201,168,76,0.65)',textTransform:'uppercase',letterSpacing:'.1em'}}>{s.tag}</span>
              </button>
            ))}
          </div>

          {/* Strategy description */}
          <div style={{background:'rgba(201,168,76,0.08)',border:'1px solid rgba(201,168,76,0.25)',borderRadius:8,padding:'11px 15px',marginBottom:22,fontFamily:"'Space Mono',monospace",fontSize:13,color:C.soft,lineHeight:1.7}}>
            {strat.desc}
          </div>

          {/* Grid: params + chart */}
          <div className="opt-grid">

            {/* Parameters */}
            <div className="opt-card">
              <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:'.18em',textTransform:'uppercase',color:C.gold,marginBottom:20}}>Parameters</div>

              {/* Strike sliders */}
              {optLegs.map((leg,i)=>{
                const legLabel=`${leg.action==='buy'?'Long':'Short'} ${leg.type==='call'?'Call':'Put'}`;
                const atmS=roundStrike(stockPrice,stockPrice);
                const pct=strikes[i]?((strikes[i]-stockPrice)/stockPrice*100):0;
                return(
                  <div key={i} style={{marginBottom:22}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,alignItems:'baseline'}}>
                      <span style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:700,color:C.soft}}>{legLabel} Strike</span>
                      <div style={{textAlign:'right'}}>
                        <span style={{fontFamily:"'Space Mono',monospace",fontSize:14,fontWeight:700,color:C.gold2}}>${strikes[i]||0}</span>
                        <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:pct>0?C.green:pct<0?C.neg:C.soft,marginLeft:6}}>{pct>0?'+':''}{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                    <input type="range" className="opt-slider"
                      min={(stockPrice*0.55).toFixed(2)}
                      max={(stockPrice*1.45).toFixed(2)}
                      step={strikeInterval(stockPrice)}
                      value={strikes[i]||stockPrice}
                      onChange={e=>{
                        const v=parseFloat(e.target.value);
                        setStrikes(prev=>{const n=[...prev];n[i]=v;return n;});
                      }}
                    />
                    <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Space Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.60)',marginTop:6}}>
                      <span>-45%</span><span style={{color:'rgba(201,168,76,0.75)'}}>ATM {fmtS(atmS)}</span><span>+45%</span>
                    </div>
                  </div>
                );
              })}

              {/* DTE */}
              <div style={{marginBottom:22}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,alignItems:'baseline'}}>
                  <span style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:700,color:C.soft}}>Days to Expiry</span>
                  <span style={{fontFamily:"'Space Mono',monospace",fontSize:14,fontWeight:700,color:dte<=1?C.neg:dte<=7?'#FF9500':C.gold2}}>
                    {dte===0?'0DTE 🔥':`${dte}d ${dte<=3?'⚡':''}`}
                  </span>
                </div>
                <input type="range" className="opt-slider" min={0} max={365} step={1} value={dte}
                  onChange={e=>setDte(parseInt(e.target.value))}/>
                <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Space Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.60)',marginTop:6}}>
                  <span style={{color:C.neg}}>0DTE</span><span>1 week</span><span>1 month</span><span>3m</span><span>1yr</span>
                </div>
              </div>

              {/* IV */}
              <div style={{marginBottom:22}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,alignItems:'baseline'}}>
                  <span style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:700,color:C.soft}}>Implied Volatility</span>
                  <span style={{fontFamily:"'Space Mono',monospace",fontSize:14,fontWeight:700,color:iv>=100?C.neg:iv>=60?'#FF9500':C.gold2}}>{iv}%</span>
                </div>
                <input type="range" className="opt-slider" min={5} max={200} step={1} value={iv}
                  onChange={e=>setIv(parseInt(e.target.value))}/>
                <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Space Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.60)',marginTop:6}}>
                  <span>Low 5%</span><span style={{color:'rgba(201,168,76,0.75)'}}>~30% normal</span><span style={{color:C.neg}}>Insane 200%</span>
                </div>
              </div>

              {/* Contracts */}
              <div>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,alignItems:'baseline'}}>
                  <span style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:700,color:C.soft}}>
                    Contracts{hasStock?' (+100 shares each)':''}
                  </span>
                  <span style={{fontFamily:"'Space Mono',monospace",fontSize:14,fontWeight:700,color:C.gold2}}>{contracts}</span>
                </div>
                <input type="range" className="opt-slider" min={1} max={50} step={1} value={contracts}
                  onChange={e=>setContracts(parseInt(e.target.value))}/>
                <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Space Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.60)',marginTop:6}}>
                  <span>1</span><span>10</span><span>25</span><span>50</span>
                </div>
              </div>
            </div>

            {/* Chart + summary */}
            <div>
              <div className="opt-card" style={{padding:'16px 16px 12px'}}>
                {/* Legend */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:'.14em',textTransform:'uppercase',color:C.gold}}>
                    P&amp;L Diagram
                  </div>
                  <div style={{display:'flex',gap:14,alignItems:'center'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5}}>
                      <svg width={22} height={8}><line x1={0} y1={4} x2={22} y2={4} stroke="rgba(255,255,255,0.75)" strokeWidth={2} strokeDasharray="4,3"/></svg>
                      <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:C.soft}}>At Expiry</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:5}}>
                      <svg width={22} height={8}><line x1={0} y1={4} x2={22} y2={4} stroke={C.gold2} strokeWidth={2.5}/></svg>
                      <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:C.soft}}>Today</span>
                    </div>
                  </div>
                </div>

                {points.length>0?(
                  <svg ref={chartRef} viewBox={`0 0 ${W} ${H}`}
                    style={{width:'100%',height:'auto',display:'block',cursor:'crosshair',userSelect:'none',touchAction:'none'}}
                    onMouseMove={handleChartMove} onMouseLeave={()=>setHoverS(null)}
                    onTouchMove={e=>{e.preventDefault();handleChartMove(e);}} onTouchEnd={()=>setHoverS(null)}>
                    <defs>
                      <clipPath id="optClip"><rect x={PL} y={PT} width={cW} height={cH}/></clipPath>
                      <linearGradient id="optProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(0,200,5,0.35)"/>
                        <stop offset="100%" stopColor="rgba(0,200,5,0.04)"/>
                      </linearGradient>
                      <linearGradient id="optLoss" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(255,59,48,0.04)"/>
                        <stop offset="100%" stopColor="rgba(255,59,48,0.32)"/>
                      </linearGradient>
                    </defs>

                    {/* Grid + Y ticks */}
                    {yTicks.map(v=>{
                      const y=yP(v); if(y<PT-6||y>PT+cH+6) return null;
                      return(
                        <g key={v}>
                          <line x1={PL} y1={y} x2={W-PR} y2={y} stroke={v===0?"rgba(255,255,255,0.28)":"rgba(255,255,255,0.10)"} strokeWidth={1}/>
                          <text x={PL-7} y={y+3.5} textAnchor="end" fontSize={12} fontFamily="monospace"
                            fill={v===0?"rgba(255,255,255,0.75)":"rgba(255,255,255,0.50)"}>
                            {v>=0?'+':''}{Math.abs(v)>=1000?`${(v/1000).toFixed(1)}K`:v.toFixed(0)}
                          </text>
                        </g>
                      );
                    })}

                    {/* Profit/loss zones */}
                    {y0>PT&&<rect x={PL} y={PT} width={cW} height={Math.min(y0-PT,cH)} fill="url(#optProfit)" clipPath="url(#optClip)"/>}
                    {y0<PT+cH&&<rect x={PL} y={Math.max(y0,PT)} width={cW} height={PT+cH-Math.max(y0,PT)} fill="url(#optLoss)" clipPath="url(#optClip)"/>}

                    {/* Zero line */}
                    <line x1={PL} y1={y0} x2={W-PR} y2={y0} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5}/>

                    {/* Expiry line */}
                    <path d={expPath} fill="none" stroke="rgba(255,255,255,0.80)" strokeWidth={2} strokeDasharray="5,4" clipPath="url(#optClip)"/>

                    {/* Today line */}
                    <path d={nowPath} fill="none" stroke={C.gold2} strokeWidth={2.5} clipPath="url(#optClip)"/>

                    {/* Current price marker */}
                    <line x1={xP(stockPrice)} y1={PT} x2={xP(stockPrice)} y2={PT+cH}
                      stroke="rgba(201,168,76,0.55)" strokeWidth={1} strokeDasharray="3,4" clipPath="url(#optClip)"/>

                    {/* Breakevens */}
                    {breakevens.map((be,bi)=>{
                      const bx=xP(be);
                      if(bx<PL||bx>W-PR) return null;
                      return(
                        <g key={bi}>
                          <circle cx={bx} cy={y0} r={3.5} fill={C.gold2}/>
                          <text x={bx} y={PT+cH+16} textAnchor="middle" fontSize={12} fontFamily="monospace" fill={C.gold}>${be.toFixed(1)}</text>
                          <text x={bx} y={PT+cH+27} textAnchor="middle" fontSize={10} fontFamily="monospace" fill="rgba(201,168,76,0.70)">BE</text>
                        </g>
                      );
                    })}

                    {/* X-axis labels */}
                    {xLabels.map((l,i)=>(
                      <text key={i} x={l.x} y={PT+cH+38} textAnchor="middle" fontSize={12} fontFamily="monospace" fill="rgba(255,255,255,0.55)">${l.S.toFixed(0)}</text>
                    ))}

                    {/* Hover */}
                    {hoverS&&hoverPt&&(()=>{
                      const hx=xP(hoverS);
                      if(hx<PL||hx>W-PR) return null;
                      const tipX=hx>W-140?hx-132:hx+10;
                      const expColor=hoverPt.pnlExp>=0?C.green:C.neg;
                      const nowColor=hoverPt.pnlNow>=0?C.green:C.neg;
                      return(
                        <g>
                          <line x1={hx} y1={PT} x2={hx} y2={PT+cH} stroke="rgba(255,255,255,0.45)" strokeWidth={1}/>
                          <circle cx={hx} cy={yP(hoverPt.pnlNow)} r={4} fill={C.gold2} stroke={C.bg} strokeWidth={1.5}/>
                          <circle cx={hx} cy={yP(hoverPt.pnlExp)} r={3} fill="rgba(255,255,255,0.9)" stroke={C.bg} strokeWidth={1}/>
                          <rect x={tipX} y={PT+2} width={120} height={58} rx={7} fill="rgba(12,12,12,0.97)" stroke="rgba(201,168,76,0.40)"/>
                          <text x={tipX+9} y={PT+17} fontSize={9.5} fontFamily="monospace" fill={C.soft}>S = {fmtS(hoverPt.S)}</text>
                          <text x={tipX+9} y={PT+32} fontSize={10} fontFamily="monospace" fill={nowColor}>Now  {fmtPnL(hoverPt.pnlNow)}</text>
                          <text x={tipX+9} y={PT+47} fontSize={10} fontFamily="monospace" fill={expColor}>Exp  {fmtPnL(hoverPt.pnlExp)}</text>
                        </g>
                      );
                    })()}
                  </svg>
                ):(
                  <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center',color:C.soft,fontFamily:"'Space Mono',monospace",fontSize:14}}>
                    Enter a ticker above to see the P&L diagram
                  </div>
                )}
              </div>

              {/* Position summary */}
              {points.length>0&&(
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginTop:10}}>
                  {[
                    {label:'Premium', value:fmtPnL(Math.abs(netDebit),false), sub:netDebit>0?'Debit paid':'Credit rcvd', color:netDebit>0?'#FF9500':C.green},
                    {label:'Max Profit', value:maxProfit==null?'Unlimited':fmtPnL(maxProfit), color:C.green},
                    {label:'Max Loss',   value:maxLoss==null?'Unlimited':fmtPnL(maxLoss),   color:C.neg},
                  ].map(({label,value,sub,color})=>(
                    <div key={label} className="opt-stat">
                      <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:C.soft,letterSpacing:'.08em',textTransform:'uppercase',marginBottom:5}}>{label}</div>
                      <div style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:800,color,lineHeight:1.1}}>{value}</div>
                      {sub&&<div style={{fontFamily:"'Space Mono',monospace",fontSize:11,color,marginTop:4,opacity:0.85}}>{sub}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Greeks */}
          {points.length>0&&(
            <div style={{marginTop:24}}>
              <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:'.18em',textTransform:'uppercase',color:C.gold,marginBottom:12}}>Position Greeks</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                {[
                  {sym:'Δ', label:'Delta', val:`${greeks.delta>=0?'+':''}${greeks.delta.toFixed(0)}`,
                   plain:`For every $1 the stock moves, your P&L changes by ~${fmtPnL(greeks.delta)}`,
                   color:greeks.delta>=0?C.green:C.neg},
                  {sym:'Γ', label:'Gamma', val:greeks.gamma.toFixed(3),
                   plain:`How fast delta accelerates — high gamma = P&L moves faster in big moves`,
                   color:C.gold2},
                  {sym:'Θ', label:'Theta', val:`${greeks.theta>=0?'+':''}${fmtPnL(greeks.theta,false)}/day`,
                   plain:`You ${greeks.theta<0?'lose':'earn'} ${fmtPnL(Math.abs(greeks.theta),false)} every single day that passes`,
                   color:greeks.theta>=0?C.green:C.neg},
                  {sym:'ν', label:'Vega', val:`${greeks.vega>=0?'+':''}${fmtPnL(greeks.vega,false)}/1%IV`,
                   plain:`Each 1% swing in implied vol = ${greeks.vega>=0?'+':''}${fmtPnL(Math.abs(greeks.vega),false)} to your position`,
                   color:C.text},
                ].map(({sym,label,val,plain,color})=>(
                  <div key={label} className="opt-greek">
                    <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:6}}>
                      <span style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color,lineHeight:1}}>{sym}</span>
                      <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:C.soft,letterSpacing:'.10em',textTransform:'uppercase'}}>{label}</span>
                    </div>
                    <div style={{fontFamily:"'Space Mono',monospace",fontSize:13,fontWeight:700,color,marginBottom:6}}>{val}</div>
                    <div style={{fontFamily:"'Space Mono',monospace",fontSize:12,color:'rgba(255,255,255,0.70)',lineHeight:1.65}}>{plain}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Theta decay chart */}
          {decayPts.length>1&&(
            <div style={{marginTop:24}}>
              <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:700,letterSpacing:'.18em',textTransform:'uppercase',color:C.gold,marginBottom:12}}>
                Theta Decay — P&L at {fmtS(stockPrice)} as Time Passes
              </div>
              <div className="opt-card" style={{padding:'14px 16px 10px'}}>
                <svg viewBox={`0 0 ${DW} ${DH}`} style={{width:'100%',height:'auto',display:'block'}}>
                  <line x1={DPL} y1={dy0} x2={DW-DPR} y2={dy0} stroke="rgba(255,255,255,0.30)" strokeWidth={1}/>
                  {dy0>DPT&&<rect x={DPL} y={DPT} width={dcW} height={Math.min(dy0-DPT,dcH)} fill="rgba(0,200,5,0.12)"/>}
                  {dy0<DPT+dcH&&<rect x={DPL} y={Math.max(dy0,DPT)} width={dcW} height={DPT+dcH-Math.max(dy0,DPT)} fill="rgba(255,59,48,0.12)"/>}
                  <path d={decayPath} fill="none" stroke={decayColor} strokeWidth={2.5}/>
                  {[0,0.25,0.5,0.75,1].map(f=>{
                    const d=Math.round(f*dte);
                    const x=dxP(d);
                    return <text key={f} x={x} y={DH-6} textAnchor="middle" fontSize={11} fontFamily="monospace" fill="rgba(255,255,255,0.65)">{d}d</text>;
                  })}
                  {decayPts.length>0&&(()=>{
                    const last=decayPts[decayPts.length-1];
                    const color=last.val>=0?C.green:C.neg;
                    return <text x={DW-DPR-2} y={dyP(last.val)-5} textAnchor="end" fontSize={11} fontFamily="monospace" fill={color}>{fmtPnL(last.val)}</text>;
                  })()}
                </svg>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{marginTop:28,fontFamily:"'Space Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.55)',lineHeight:1.8,borderTop:'1px solid rgba(201,168,76,0.18)',paddingTop:16}}>
            Theoretical prices use Black-Scholes. IV is user-input — not live market IV. This is for educational modeling only, not financial advice. Options can expire worthless. Never risk more than you can afford to lose.
          </div>

        </>}

        {!stockPrice&&(
          <div style={{marginTop:40,display:'flex',flexDirection:'column',alignItems:'center',gap:16,opacity:0.75}}>
            <div style={{fontSize:48}}>🎰</div>
            <div style={{fontFamily:"'Space Mono',monospace",fontSize:14,color:C.soft,textAlign:'center'}}>Enter a ticker to build your position</div>
          </div>
        )}

      </div>
      <SiteFooter/>
    </>
  );
}
